import { useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CallMadeIcon from '@mui/icons-material/CallMade'
import CallReceivedIcon from '@mui/icons-material/CallReceived'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { capitalize } from 'lodash'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'
import { acceptAssetTransfer, rejectAssetTransfer } from 'store/asset/need-attention'
import Header from 'components/Header'
import { useSession } from 'components/Providers'

function DetailRow({ label, value }) {
  return (
    <>
      <Grid item xs={5}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Grid>
      <Grid item xs={7}>
        <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
      </Grid>
    </>
  )
}

export default function DevicesToAccept({ expanded, onChange }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const list = useSelector(state => state.asset.needsAttention.pendingAcceptance) || []
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const { campus } = useSession();
  const sendingCampus = selected?.sendingCampus ? campus.id === selected.sendingCampus.id : null
  const receivingCampus = selected?.receivingCampus ? campus.id === selected.receivingCampus.id : null

  const handleAction = async action => {
    setLoading(true)
    await dispatch(action({ id: selected.id }))
    setLoading(false)
    setSelected(null)
  }

  const columns = [
    { field: 'assetNumber', headerName: t('Asset Number'), flex: 1, renderCell: p => p.row.asset?.assetNumber },
    { field: 'brand', headerName: t('Brand'), flex: 1, renderCell: p => capitalize(p.row.asset?.brand || '') },
    { field: 'model', headerName: t('Model'), flex: 1, renderCell: p => p.row.asset?.model },
    { field: 'serialNumber', headerName: t('Serial Number'), flex: 1, renderCell: p => p.row.asset?.serialNumber },
    {
      field: 'sendingCampus',
      headerName: t('From Campus'),
      flex: 1.5,
      renderCell: p => (
        <Chip
          icon={<CallMadeIcon color='primary' />}
          label={p.row.sendingCampus?.name}
          size="small"
          color={campus?.id === p.row.sendingCampus?.id ? 'error' : 'default'}
          variant="filled"
          sx={{ '& .MuiChip-icon': { bgcolor: 'white', borderRadius: '50%', p: 0.25 } }}
        />
      ),
    },
    {
      field: 'receivingCampus',
      headerName: t('To Campus'),
      flex: 1.5,
      renderCell: p => (
        <Chip
          icon={<CallReceivedIcon color='primary' />}
          label={p.row.receivingCampus?.name}
          size="small"
          color={campus?.id === p.row.receivingCampus?.id ? 'success' : 'default'}
          variant="filled"
          sx={{ '& .MuiChip-icon': { bgcolor: 'white', borderRadius: '50%', p: 0.25 } }}
        />
      ),
    },
    {
      field: 'sentAt',
      headerName: t('Transfer Date'),
      flex: 1,
      renderCell: p => p.row.sentAt ? new Date(p.row.sentAt).toLocaleDateString() : '',
    },
    {
      field: 'actions',
      headerName: t('Actions'),
      width: 110,
      sortable: false,
      renderCell: p => (
        <Button size="small" variant="outlined" onClick={() => setSelected(p.row)}>
          {t('Review')}
        </Button>
      ),
    },
  ]

  return (
    <>
      <Accordion expanded={expanded} onChange={onChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body1" fontWeight={600}>{t('Devices to Accept')}</Typography>
            <Badge badgeContent={list.length} color="warning" />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <DataGrid
            rows={list}
            columns={columns}
            pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
            disableRowSelectionOnClick
            autoHeight
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          />
        </AccordionDetails>
      </Accordion>

      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        {selected && (
          <>
            <Header
              title={t('Transfer Review')}
              subtitle={selected.asset?.assetNumber}
              close={() => setSelected(null)}
              borderBottom
              small
            />

            <Box sx={{ px: 3, py: 2.5 }}>
              <Grid container spacing={1.5} rowSpacing={2}>
                <DetailRow label={t('Asset Number')} value={selected.asset?.assetNumber} />
                <DetailRow label={t('Brand')} value={capitalize(selected.asset?.brand || '')} />
                <DetailRow label={t('Model')} value={selected.asset?.model} />
                <DetailRow label={t('Serial Number')} value={selected.asset?.serialNumber} />
                <DetailRow label={t('From Campus')} value={selected.sendingCampus?.name} />
                <DetailRow label={t('To Campus')} value={selected.receivingCampus?.name} />
                <DetailRow label={t('Assigned To')} value={selected.asset?.assignInfo?.name || t('Not Assigned')} />
                <DetailRow label={t('Sent By')} value={`${selected.sendingPerson?.firstName} ${selected.sendingPerson?.lastName}`} />
                <DetailRow label={t('Transfer Date')} value={selected.sentAt ? new Date(selected.sentAt).toLocaleDateString() : ''} />
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {sendingCampus && (
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={loading}
                    onClick={() => handleAction(rejectAssetTransfer)}
                  >
                    {t('Cancel')}
                  </Button>
                )}
                {receivingCampus && (
                  <Button
                    variant="contained"
                    color="success"
                    disabled={loading}
                    onClick={() => handleAction(acceptAssetTransfer)}
                  >
                    {t('Accept')}
                  </Button>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Drawer>
    </>
  )
}
